import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogs } from '../utils/database';
import { Helmet } from 'react-helmet-async';
import { ImageModal } from '../utils/ImageModal';
import { getImageUrl } from '../utils/imageOptimization';
import RichContentSection from '../components/BlogArticle/RichContentSection';

const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  const [openFaqs, setOpenFaqs] = useState(new Set());

  const loadBlogArticle = useCallback(async () => {
    try {
      setLoading(true);
      const allBlogs = await getBlogs();
      const foundBlog = allBlogs.find((b) => b.slug === slug && b.published);

      if (!foundBlog) {
        setError('Blog post not found');
        return;
      }

      setBlog(foundBlog);

      // Collect all images from the blog post for the modal
      const images = [];
      if (foundBlog.featuredImage) {
        images.push(foundBlog.featuredImage);
      }
      if (foundBlog.beforeImage) {
        images.push(foundBlog.beforeImage);
      }
      if (foundBlog.afterImage) {
        images.push(foundBlog.afterImage);
      }
      if (foundBlog.gallery && foundBlog.gallery.length > 0) {
        images.push(...foundBlog.gallery);
      }

      // Collect images from new dynamic sections
      if (
        foundBlog.beforeProcessSections &&
        foundBlog.beforeProcessSections.length > 0
      ) {
        foundBlog.beforeProcessSections.forEach((section) => {
          if (section.images && section.images.length > 0) {
            images.push(...section.images);
          }
        });
      }

      if (foundBlog.processSections && foundBlog.processSections.length > 0) {
        foundBlog.processSections.forEach((section) => {
          if (section.images && section.images.length > 0) {
            images.push(...section.images);
          }
        });
      }

      if (
        foundBlog.afterProcessSections &&
        foundBlog.afterProcessSections.length > 0
      ) {
        foundBlog.afterProcessSections.forEach((section) => {
          if (section.images && section.images.length > 0) {
            images.push(...section.images);
          }
        });
      }
      setAllImages(images);

      // Load related blogs (same job type, excluding current blog)
      const related = allBlogs
        .filter(
          (b) =>
            b.published &&
            b.id !== foundBlog.id &&
            b.jobType === foundBlog.jobType
        )
        .slice(0, 3);
      setRelatedBlogs(related);
    } catch (error) {
      console.error('Error loading blog article:', error);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadBlogArticle();
  }, [loadBlogArticle]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatProjectDate = (startDate, endDate) => {
    if (!startDate) return '';
    const start = new Date(startDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });

    if (!endDate) return start;

    const end = new Date(endDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });

    return `${start} - ${end}`;
  };

  const getJobTypeColor = (jobType) => {
    switch (jobType?.toLowerCase()) {
      case 'kitchen remodel':
        return 'bg-orange-100 text-orange-800';
      case 'bathroom remodel':
        return 'bg-blue-100 text-blue-800';
      case 'custom cabinets':
        return 'bg-green-100 text-green-800';
      case 'whole home':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getClientTypeIcon = (clientType) => {
    switch (clientType?.toLowerCase()) {
      case 'residential':
        return '🏠';
      case 'commercial':
        return '🏢';
      case 'luxury':
        return '💎';
      default:
        return '🏠';
    }
  };

  const openImageModal = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    setModalOpen(true);
  };

  const getImageIndex = (imageUrl) => {
    return allImages.findIndex((img) => img.url === imageUrl);
  };

  const toggleFaq = (index) => {
    const newOpenFaqs = new Set(openFaqs);
    if (newOpenFaqs.has(index)) {
      newOpenFaqs.delete(index);
    } else {
      newOpenFaqs.add(index);
    }
    setOpenFaqs(newOpenFaqs);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-[#7E5A3C]">
            <svg
              className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading article...
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="px-4 mx-auto max-w-md text-center">
          <svg
            className="mx-auto w-12 h-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Blog post not found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <div className="mt-6">
            <Link
              to="/blog"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#7E5A3C] hover:bg-[#8B6B4A]">
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{blog.metaTitle || blog.title} | Oak & Stone Remodel</title>
        <meta
          name="description"
          content={blog.metaDescription || blog.excerpt || blog.introParagraph}
        />
        <meta
          name="keywords"
          content={
            blog.primaryKeyword && blog.secondaryKeywords
              ? `${blog.primaryKeyword}, ${
                  Array.isArray(blog.secondaryKeywords)
                    ? blog.secondaryKeywords.join(', ')
                    : blog.secondaryKeywords
                }`
              : Array.isArray(blog.tags)
              ? blog.tags.join(', ')
              : blog.tags
          }
        />
        <meta property="og:title" content={blog.metaTitle || blog.title} />
        <meta
          property="og:description"
          content={blog.metaDescription || blog.excerpt || blog.introParagraph}
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://oakandstoneremodel.com/blog/${blog.slug}`}
        />
        {blog.featuredImage && (
          <meta
            property="og:image"
            content={getImageUrl(blog.featuredImage.url, 'large')}
          />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.metaTitle || blog.title} />
        <meta
          name="twitter:description"
          content={blog.metaDescription || blog.excerpt || blog.introParagraph}
        />
        {blog.featuredImage && (
          <meta
            name="twitter:image"
            content={getImageUrl(blog.featuredImage.url, 'large')}
          />
        )}
        <link
          rel="canonical"
          href={`https://oakandstoneremodel.com/blog/${blog.slug}`}
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb Navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 py-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-4">
                <li>
                  <Link to="/" className="text-gray-500 hover:text-gray-700">
                    Home
                  </Link>
                </li>
                <li>
                  <svg
                    className="flex-shrink-0 w-5 h-5 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="text-gray-500 hover:text-gray-700">
                    Blog
                  </Link>
                </li>
                <li>
                  <svg
                    className="flex-shrink-0 w-5 h-5 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </li>
                <li
                  className="font-medium text-gray-900 truncate"
                  aria-current="page">
                  {blog.title}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <div className="bg-white">
          <div className="px-4 py-12 mx-auto max-w-6xl sm:px-6 lg:px-8">
            {/* Meta Information */}
            <div className="flex justify-between items-center mb-8">
              <span
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getJobTypeColor(
                  blog.jobType
                )}`}>
                {blog.jobType || 'Remodeling'}
              </span>
              <span className="text-sm text-gray-500">
                {formatDate(blog.createdAt)}
              </span>
            </div>

            {/* Title */}
            <h1 className="mb-8 text-5xl font-bold leading-tight text-gray-900 md:text-6xl">
              {blog.title}
            </h1>

            {/* Excerpt */}
            {blog.excerpt && (
              <p className="mb-10 text-2xl leading-relaxed text-gray-600">
                {blog.excerpt}
              </p>
            )}

            {/* Author and Location */}
            <div className="flex flex-col gap-6 mb-10 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-6">
                {blog.author && (
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-[#7E5A3C] rounded-full flex items-center justify-center">
                      <span className="text-base font-semibold text-white">
                        {blog.author
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-4">
                      <p className="text-base font-medium text-gray-900">
                        {blog.author}
                      </p>
                      <p className="text-sm text-gray-500">
                        Oak & Stone Remodel
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {blog.location && (
                <div className="flex items-center text-base text-gray-500">
                  <svg
                    className="mr-2 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {blog.location}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {blog.featuredImage && (
          <div className="bg-gray-100">
            <div className="px-4 py-12 mx-auto max-w-6xl sm:px-6 lg:px-8">
              <div
                className="relative cursor-pointer group"
                onClick={() =>
                  openImageModal(getImageIndex(blog.featuredImage.url))
                }>
                <img
                  src={getImageUrl(blog.featuredImage.url, 'large')}
                  alt={blog.featuredImage.alt || blog.title}
                  className="w-full h-80 md:h-[500px] object-cover rounded-xl transition-all duration-200"
                />
                <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-xl transition-all duration-200 group-hover:bg-opacity-10">
                  <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <svg
                      className="w-12 h-12 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Article Content */}
        <div className="bg-white">
          <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {/* Intro Paragraph */}
                {blog.introParagraph && (
                  <div className="mb-12">
                    <p className="text-lg leading-relaxed text-gray-700">
                      {blog.introParagraph}
                    </p>
                  </div>
                )}

                {/* Rich Content Sections - After Intro */}
                {blog.richContentSections &&
                  blog.richContentSections.length > 0 &&
                  blog.richContentSections
                    .filter(
                      (section) =>
                        section.position === 'after-intro' &&
                        (section.title || section.content)
                    )
                    .map((section, index) => (
                      <div key={section.id || index} className="mb-12">
                        <RichContentSection section={section} />
                      </div>
                    ))}

                {/* Before and After Comparison Section */}
                {(blog.beforeSection ||
                  blog.afterSection ||
                  blog.beforeImage ||
                  blog.afterImage) && (
                  <div className="mb-16">
                    <div className="mb-12 text-center">
                      <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-blue-100 rounded-full">
                        <svg
                          className="w-8 h-8 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                          />
                        </svg>
                      </div>
                      <h2 className="mb-4 text-4xl font-bold text-gray-900">
                        Project Transformation
                      </h2>
                      <div className="mx-auto w-24 h-1 bg-blue-500 rounded-full"></div>
                    </div>

                    {/* Before and After Images Side by Side */}
                    {(blog.beforeImage || blog.afterImage) && (
                      <div className="mb-12 space-y-8">
                        {/* Before Image and Text */}
                        {blog.beforeImage && (
                          <div className="group">
                            <div
                              className="relative cursor-pointer group"
                              onClick={() =>
                                openImageModal(
                                  getImageIndex(blog.beforeImage.url)
                                )
                              }>
                              <img
                                src={getImageUrl(blog.beforeImage.url, 'large')}
                                alt={
                                  blog.beforeImage.alt || 'Before the project'
                                }
                                className="w-full h-64 md:h-96 lg:h-[500px] object-cover rounded-xl transition-all duration-200"
                              />
                              <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-xl transition-all duration-200 group-hover:bg-opacity-10">
                                <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                  <svg
                                    className="w-8 h-8 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            {blog.beforeSection && (
                              <div className="mt-6">
                                <div className="p-6 bg-gray-50 rounded-xl border-l-4 border-blue-400">
                                  <h3 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                                    <span className="inline-flex justify-center items-center mr-3 w-6 h-6 text-sm font-bold text-white bg-blue-500 rounded-full">
                                      !
                                    </span>
                                    Before the Project
                                  </h3>
                                  <p className="text-lg leading-relaxed text-gray-700">
                                    {blog.beforeSection}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* After Image and Text */}
                        {blog.afterImage && (
                          <div className="group">
                            <div
                              className="relative cursor-pointer group"
                              onClick={() =>
                                openImageModal(
                                  getImageIndex(blog.afterImage.url)
                                )
                              }>
                              <img
                                src={getImageUrl(blog.afterImage.url, 'large')}
                                alt={blog.afterImage.alt || 'After the project'}
                                className="w-full h-64 md:h-96 lg:h-[500px] object-cover rounded-xl transition-all duration-200"
                              />
                              <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-xl transition-all duration-200 group-hover:bg-opacity-10">
                                <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                  <svg
                                    className="w-8 h-8 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            {blog.afterSection && (
                              <div className="mt-6">
                                <div className="p-6 bg-gray-50 rounded-xl border-l-4 border-green-400">
                                  <h3 className="flex items-center mb-4 text-xl font-semibold text-gray-900">
                                    <span className="inline-flex justify-center items-center mr-3 w-6 h-6 text-sm font-bold text-white bg-green-500 rounded-full">
                                      ✓
                                    </span>
                                    After the Project
                                  </h3>
                                  <p className="text-lg leading-relaxed text-gray-700">
                                    {blog.afterSection}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Rich Content Sections - Before Process */}
                {blog.richContentSections &&
                  blog.richContentSections.length > 0 &&
                  blog.richContentSections
                    .filter(
                      (section) =>
                        section.position === 'before-process' &&
                        (section.title || section.content)
                    )
                    .map((section, index) => (
                      <div key={section.id || index} className="mb-12">
                        <RichContentSection section={section} />
                      </div>
                    ))}

                {/* Before Process Sections */}
                {blog.beforeProcessSections &&
                  blog.beforeProcessSections.length > 0 &&
                  blog.beforeProcessSections.some(
                    (section) =>
                      section.title ||
                      section.description ||
                      (section.images && section.images.length > 0)
                  ) && (
                    <div className="mb-16">
                      <div className="mb-12 text-center">
                        <div className="inline-flex justify-center items-center mb-4 w-16 h-16 rounded-full bg-primary/10">
                          <svg
                            className="w-8 h-8 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <h2 className="mb-4 text-4xl font-bold text-gray-900">
                          Project Overview
                        </h2>
                        <div className="mx-auto w-24 h-1 rounded-full bg-primary"></div>
                      </div>
                      <div className="space-y-8">
                        {blog.beforeProcessSections
                          .filter(
                            (section) =>
                              section.title ||
                              section.description ||
                              (section.images && section.images.length > 0)
                          )
                          .map((section, index) => (
                            <div
                              key={section.id || index}
                              className="p-8 rounded-xl border-l-4 bg-primary/5 border-primary">
                              {section.title && (
                                <h3 className="mb-6 text-2xl font-semibold text-gray-900">
                                  {section.title}
                                </h3>
                              )}
                              {section.description && (
                                <p className="mb-6 text-lg leading-relaxed text-gray-700">
                                  {section.description}
                                </p>
                              )}
                              {section.images && section.images.length > 0 && (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                  {section.images.map((image, imageIndex) => (
                                    <div
                                      key={imageIndex}
                                      className="relative cursor-pointer group"
                                      onClick={() =>
                                        openImageModal(getImageIndex(image.url))
                                      }>
                                      <img
                                        src={getImageUrl(image.url, 'medium')}
                                        alt={
                                          image.alt ||
                                          `${section.title} image ${
                                            imageIndex + 1
                                          }`
                                        }
                                        className="object-cover w-full h-48 rounded-lg transition-all duration-200"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Process Sections */}
                {blog.processSections &&
                  blog.processSections.length > 0 &&
                  blog.processSections.some(
                    (section) =>
                      section.title ||
                      section.description ||
                      (section.images && section.images.length > 0)
                  ) && (
                    <div className="mb-16">
                      <div className="mb-12 text-center">
                        <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-orange-100 rounded-full">
                          <svg
                            className="w-8 h-8 text-orange-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                        <h2 className="mb-4 text-4xl font-bold text-gray-900">
                          Our Process
                        </h2>
                        <div className="mx-auto w-24 h-1 bg-orange-500 rounded-full"></div>
                      </div>
                      <div className="space-y-8">
                        {blog.processSections
                          .filter(
                            (section) =>
                              section.title ||
                              section.description ||
                              (section.images && section.images.length > 0)
                          )
                          .map((section, index) => (
                            <div
                              key={section.id || index}
                              className="p-8 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-l-4 border-orange-400">
                              {section.title && (
                                <h3 className="mb-6 text-2xl font-semibold text-gray-900">
                                  {section.title}
                                </h3>
                              )}
                              {section.description && (
                                <p className="mb-6 text-lg leading-relaxed text-gray-700">
                                  {section.description}
                                </p>
                              )}
                              {section.images && section.images.length > 0 && (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                  {section.images.map((image, imageIndex) => (
                                    <div
                                      key={imageIndex}
                                      className="relative cursor-pointer group"
                                      onClick={() =>
                                        openImageModal(getImageIndex(image.url))
                                      }>
                                      <img
                                        src={getImageUrl(image.url, 'medium')}
                                        alt={
                                          image.alt ||
                                          `${section.title} image ${
                                            imageIndex + 1
                                          }`
                                        }
                                        className="object-cover w-full h-48 rounded-lg transition-all duration-200"
                                      />
                                      <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-lg transition-all duration-200 group-hover:bg-opacity-10">
                                        <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                          <svg
                                            className="w-6 h-6 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Rich Content Sections - After Process */}
                {blog.richContentSections &&
                  blog.richContentSections.length > 0 &&
                  blog.richContentSections
                    .filter(
                      (section) =>
                        section.position === 'after-process' &&
                        (section.title || section.content)
                    )
                    .map((section, index) => (
                      <div key={section.id || index} className="mb-12">
                        <RichContentSection section={section} />
                      </div>
                    ))}

                {/* After Process Sections */}
                {blog.afterProcessSections &&
                  blog.afterProcessSections.length > 0 &&
                  blog.afterProcessSections.some(
                    (section) =>
                      section.title ||
                      section.description ||
                      (section.images && section.images.length > 0)
                  ) && (
                    <div className="mb-16">
                      <div className="mb-12 text-center">
                        <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-purple-100 rounded-full">
                          <svg
                            className="w-8 h-8 text-purple-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h2 className="mb-4 text-4xl font-bold text-gray-900">
                          Project Results
                        </h2>
                        <div className="mx-auto w-24 h-1 bg-purple-500 rounded-full"></div>
                      </div>
                      <div className="space-y-8">
                        {blog.afterProcessSections
                          .filter(
                            (section) =>
                              section.title ||
                              section.description ||
                              (section.images && section.images.length > 0)
                          )
                          .map((section, index) => (
                            <div
                              key={section.id || index}
                              className="p-8 bg-purple-50 rounded-xl border-l-4 border-purple-400">
                              {section.title && (
                                <h3 className="mb-6 text-2xl font-semibold text-gray-900">
                                  {section.title}
                                </h3>
                              )}
                              {section.description && (
                                <p className="mb-6 text-lg leading-relaxed text-gray-700">
                                  {section.description}
                                </p>
                              )}
                              {section.images && section.images.length > 0 && (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                  {section.images.map((image, imageIndex) => (
                                    <div
                                      key={imageIndex}
                                      className="relative cursor-pointer group"
                                      onClick={() =>
                                        openImageModal(getImageIndex(image.url))
                                      }>
                                      <img
                                        src={getImageUrl(image.url, 'medium')}
                                        alt={
                                          image.alt ||
                                          `${section.title} image ${
                                            imageIndex + 1
                                          }`
                                        }
                                        className="object-cover w-full h-48 rounded-lg shadow-md transition-transform duration-200 group-hover:scale-105"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Legacy Process Section (for backward compatibility) */}
                {blog.processSection &&
                  blog.processSection.trim() &&
                  (!blog.processSections ||
                    blog.processSections.length === 0) && (
                    <div className="mb-12">
                      <h2 className="mb-6 text-3xl font-bold text-gray-900">
                        Our Process
                      </h2>
                      <p className="text-lg leading-relaxed text-gray-700">
                        {blog.processSection}
                      </p>
                    </div>
                  )}

                {/* Rich Content Sections - Before Call to Action */}
                {blog.richContentSections &&
                  blog.richContentSections.length > 0 &&
                  blog.richContentSections
                    .filter(
                      (section) =>
                        section.position === 'before-cta' &&
                        (section.title || section.content)
                    )
                    .map((section, index) => (
                      <div key={section.id || index} className="mb-12">
                        <RichContentSection section={section} />
                      </div>
                    ))}

                {/* Client Testimonial */}
                {blog.clientTestimonial && blog.clientTestimonial.trim() && (
                  <div className="mb-12">
                    <h2 className="mb-6 text-3xl font-bold text-gray-900">
                      Client Testimonial
                    </h2>
                    <blockquote className="p-10 text-white rounded-xl bg-primary">
                      <div className="flex items-start">
                        <svg
                          className="mt-2 mr-6 w-10 h-10 text-white/80"
                          fill="currentColor"
                          viewBox="0 0 24 24">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                        <p className="text-xl italic leading-relaxed">
                          "{blog.clientTestimonial}"
                        </p>
                      </div>
                    </blockquote>
                  </div>
                )}

                {/* FAQ Section */}
                {blog.faqs &&
                  blog.faqs.length > 0 &&
                  blog.faqs.some(
                    (faq) =>
                      faq.question &&
                      faq.question.trim() &&
                      faq.answer &&
                      faq.answer.trim()
                  ) && (
                    <div className="mb-16">
                      <div className="mb-12 text-center">
                        <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-indigo-100 rounded-full">
                          <svg
                            className="w-8 h-8 text-indigo-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h2 className="mb-4 text-4xl font-bold text-gray-900">
                          Frequently Asked Questions
                        </h2>
                        <div className="mx-auto w-24 h-1 bg-indigo-500 rounded-full"></div>
                      </div>
                      <div className="space-y-4">
                        {blog.faqs
                          .filter(
                            (faq) =>
                              faq.question &&
                              faq.question.trim() &&
                              faq.answer &&
                              faq.answer.trim()
                          )
                          .map((faq, index) => (
                            <div key={index} className="group">
                              <div
                                className="p-6 bg-white rounded-xl border border-gray-200 transition-all duration-200 cursor-pointer hover:shadow-lg group-hover:border-indigo-300"
                                onClick={() => toggleFaq(index)}>
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h3 className="mb-3 text-xl font-semibold text-gray-900 transition-colors duration-200 group-hover:text-indigo-600">
                                      {faq.question}
                                    </h3>
                                    {openFaqs.has(index) && (
                                      <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-l-4 border-indigo-400">
                                        <p className="leading-relaxed text-gray-700">
                                          {faq.answer}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-shrink-0 ml-4">
                                    <div className="flex justify-center items-center w-8 h-8 bg-indigo-100 rounded-full transition-all duration-200 group-hover:bg-indigo-200">
                                      <svg
                                        className={`w-4 h-4 text-indigo-600 transition-transform duration-200 ${
                                          openFaqs.has(index)
                                            ? 'rotate-180'
                                            : ''
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Tags */}
                {blog.tags &&
                  blog.tags.length > 0 &&
                  blog.tags.some((tag) => tag && tag.trim()) && (
                    <div className="mb-12">
                      <h3 className="mb-6 text-2xl font-semibold text-gray-900">
                        Tags
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {blog.tags
                          .filter((tag) => tag && tag.trim())
                          .map((tag, index) => (
                            <Link
                              key={index}
                              to={`/blog?search=${encodeURIComponent(tag)}`}
                              className="inline-flex items-center px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-full transition-all duration-200 transform hover:bg-primary hover:text-white hover:shadow-md hover:-translate-y-1">
                              <span className="mr-1">#</span>
                              {tag}
                            </Link>
                          ))}
                      </div>
                    </div>
                  )}

                {/* View All Images and CTA Section - Moved to end */}
                <div className="mb-16">
                  {/* View All Images Button */}
                  {allImages.length > 1 && (
                    <div className="mb-8">
                      <div className="p-8 text-center bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl">
                        <div className="inline-flex justify-center items-center mb-6 w-16 h-16 rounded-full bg-primary">
                          <svg
                            className="w-8 h-8 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <h3 className="mb-4 text-2xl font-bold text-gray-900">
                          View Complete Project Gallery
                        </h3>
                        <p className="mb-6 text-gray-600">
                          Explore all {allImages.length} project images in high
                          resolution
                        </p>
                        <button
                          onClick={() => openImageModal(0)}
                          className="inline-flex items-center px-8 py-4 text-lg font-medium text-white rounded-xl shadow-lg transition-all duration-200 transform bg-primary hover:bg-primary-hover hover:shadow-xl hover:-translate-y-1">
                          <svg
                            className="mr-3 w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          View All Images
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Call to Action */}
                  {blog.callToActionText && (
                    <div className="overflow-hidden relative">
                      <div className="relative p-12 text-white bg-gradient-to-br rounded-2xl from-primary via-primary-hover to-primary">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <svg
                            className="w-full h-full"
                            viewBox="0 0 100 100"
                            xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <pattern
                                id="grid"
                                width="10"
                                height="10"
                                patternUnits="userSpaceOnUse">
                                <path
                                  d="M 10 0 L 0 0 0 10"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="0.5"
                                />
                              </pattern>
                            </defs>
                            <rect width="100" height="100" fill="url(#grid)" />
                          </svg>
                        </div>

                        <div className="relative z-10">
                          <div className="mb-8 text-center">
                            <div className="inline-flex justify-center items-center mb-6 w-20 h-20 rounded-full backdrop-blur-sm bg-white/20">
                              <svg
                                className="w-10 h-10 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 10V3L4 14h7v7l9-11h-7z"
                                />
                              </svg>
                            </div>
                            <h2 className="mb-4 text-4xl font-bold">
                              Ready to Start Your Project?
                            </h2>
                            <p className="mx-auto max-w-3xl text-xl leading-relaxed opacity-90">
                              {blog.callToActionText}
                            </p>
                          </div>

                          <div className="flex flex-col gap-6 justify-center sm:flex-row">
                            <Link
                              to="/contact"
                              className="inline-flex items-center px-10 py-5 text-lg font-semibold bg-white rounded-xl shadow-lg transition-all duration-200 transform text-primary hover:bg-gray-50 hover:shadow-xl hover:-translate-y-1">
                              <svg
                                className="mr-3 w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                              </svg>
                              Get Free Consultation
                            </Link>
                            {blog.callToActionLink && (
                              <a
                                href={blog.callToActionLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-10 py-5 text-lg font-semibold text-white rounded-xl border-2 border-white backdrop-blur-sm transition-all duration-200 hover:bg-white hover:text-primary">
                                <svg
                                  className="mr-3 w-6 h-6"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                  />
                                </svg>
                                Learn More
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-2">
                <div className="sticky top-24 space-y-4">
                  {/* Project Details */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="mb-3 text-base font-semibold text-gray-900">
                      Project Details
                    </h3>
                    <div className="space-y-3">
                      {blog.jobType && (
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-16 text-xs font-medium text-gray-600">
                            Type:
                          </span>
                          <Link
                            to={`/blog?search=${encodeURIComponent(
                              blog.jobType
                            )}`}
                            className="text-xs text-blue-600 break-words transition-colors duration-200 hover:text-blue-700 hover:underline">
                            {blog.jobType}
                          </Link>
                        </div>
                      )}
                      {blog.clientType && (
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-16 text-xs font-medium text-gray-600">
                            Client:
                          </span>
                          <span className="flex items-center text-xs text-gray-900 break-words">
                            <span className="mr-1">
                              {getClientTypeIcon(blog.clientType)}
                            </span>
                            {blog.clientType}
                          </span>
                        </div>
                      )}
                      {blog.projectStartDate && (
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-16 text-xs font-medium text-gray-600">
                            Project:
                          </span>
                          <span className="text-xs text-gray-900 break-words">
                            {formatProjectDate(
                              blog.projectStartDate,
                              blog.projectEndDate
                            )}
                          </span>
                        </div>
                      )}
                      {blog.location && (
                        <div className="flex items-start">
                          <span className="flex-shrink-0 w-16 text-xs font-medium text-gray-600">
                            Location:
                          </span>
                          <span className="text-xs text-gray-900 break-words">
                            {blog.location}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SEO Keywords */}
                  {(blog.primaryKeyword || blog.secondaryKeywords) && (
                    <div className="p-4 rounded-xl bg-primary/5">
                      <h3 className="mb-3 text-base font-semibold text-gray-900">
                        Keywords
                      </h3>
                      <div className="space-y-3">
                        {blog.primaryKeyword && (
                          <div>
                            <span className="text-xs font-medium text-blue-800">
                              Primary:
                            </span>
                            <div className="mt-1">
                              <Link
                                to={`/blog?search=${encodeURIComponent(
                                  blog.primaryKeyword
                                )}`}
                                className="inline-flex items-center px-2 py-1 text-xs rounded-full transition-all duration-200 bg-primary/10 text-primary hover:bg-primary/20 hover:shadow-sm">
                                <span className="mr-1">#</span>
                                {blog.primaryKeyword}
                              </Link>
                            </div>
                          </div>
                        )}
                        {blog.secondaryKeywords && (
                          <div>
                            <span className="text-xs font-medium text-primary">
                              Secondary:
                            </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Array.isArray(blog.secondaryKeywords)
                                ? blog.secondaryKeywords.map(
                                    (keyword, index) => (
                                      <Link
                                        key={index}
                                        to={`/blog?search=${encodeURIComponent(
                                          keyword.trim()
                                        )}`}
                                        className="inline-flex items-center px-2 py-1 text-xs rounded-full transition-all duration-200 bg-primary/10 text-primary hover:bg-primary/20 hover:shadow-sm">
                                        <span className="mr-1">#</span>
                                        {keyword.trim()}
                                      </Link>
                                    )
                                  )
                                : blog.secondaryKeywords
                                    .split(',')
                                    .map((keyword, index) => (
                                      <Link
                                        key={index}
                                        to={`/blog?search=${encodeURIComponent(
                                          keyword.trim()
                                        )}`}
                                        className="inline-flex items-center px-2 py-1 text-xs rounded-full transition-all duration-200 bg-primary/10 text-primary hover:bg-primary/20 hover:shadow-sm">
                                        <span className="mr-1">#</span>
                                        {keyword.trim()}
                                      </Link>
                                    ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Internal Links */}
                  {blog.internalLinks && (
                    <div className="p-4 bg-green-50 rounded-xl">
                      <h3 className="mb-3 text-base font-semibold text-gray-900">
                        Related Links
                      </h3>
                      <div className="space-y-1">
                        {blog.internalLinks.split(',').map((link, index) => (
                          <a
                            key={index}
                            href={link.trim()}
                            className="block text-xs text-green-700 underline break-words hover:text-green-800">
                            {link.trim()}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gallery Preview */}
                  {blog.gallery && blog.gallery.length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-base font-semibold text-gray-900">
                          Project Gallery
                        </h3>
                        {allImages.length > 1 && (
                          <button
                            onClick={() => openImageModal(0)}
                            className="flex items-center text-xs font-medium text-primary hover:text-primary-hover">
                            <svg
                              className="mr-1 w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                              />
                            </svg>
                            View All ({allImages.length})
                          </button>
                        )}
                      </div>

                      {/* Improved Gallery Layout */}
                      <div className="space-y-3">
                        {/* Featured Image */}
                        {blog.gallery.length > 0 && (
                          <div
                            className="relative cursor-pointer group"
                            onClick={() =>
                              openImageModal(getImageIndex(blog.gallery[0].url))
                            }>
                            <img
                              src={getImageUrl(blog.gallery[0].url, 'medium')}
                              alt={
                                blog.gallery[0].alt || 'Featured project image'
                              }
                              className="object-cover w-full h-28 rounded-lg transition-all duration-200"
                            />
                            <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-lg transition-all duration-200 group-hover:bg-opacity-10">
                              <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                <svg
                                  className="w-5 h-5 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Thumbnail Row */}
                        {blog.gallery.length > 1 && (
                          <div className="grid grid-cols-4 gap-2">
                            {blog.gallery.slice(1, 5).map((image, index) => (
                              <div
                                key={index + 1}
                                className="relative cursor-pointer group"
                                onClick={() =>
                                  openImageModal(getImageIndex(image.url))
                                }>
                                <img
                                  src={getImageUrl(image.url, 'thumbnail')}
                                  alt={
                                    image.alt || `Project image ${index + 2}`
                                  }
                                  className="object-cover w-full h-16 rounded-lg transition-all duration-200"
                                />
                                <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-lg transition-all duration-200 group-hover:bg-opacity-10">
                                  <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                    <svg
                                      className="w-3 h-3 text-white"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24">
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {blog.gallery.length > 5 && (
                              <div
                                className="flex relative justify-center items-center h-16 bg-gray-200 rounded-lg cursor-pointer group"
                                onClick={() => openImageModal(0)}>
                                <div className="text-center">
                                  <svg
                                    className="mx-auto mb-1 w-4 h-4 text-gray-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                    />
                                  </svg>
                                  <span className="text-xs font-medium text-gray-600">
                                    +{blog.gallery.length - 5}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <div className="bg-gray-50">
            <div className="px-4 py-16 mx-auto max-w-6xl sm:px-6 lg:px-8">
              <h2 className="mb-12 text-4xl font-bold text-center text-gray-900">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                {relatedBlogs.map((relatedBlog) => (
                  <article
                    key={relatedBlog.id}
                    className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-lg transition-shadow duration-300 hover:shadow-xl">
                    {relatedBlog.featuredImage && (
                      <div
                        className="relative cursor-pointer group"
                        onClick={() => {
                          // For related articles, we could open a modal with just that image
                          // or navigate to the article. For now, let's just navigate.
                          navigate(`/blog/${relatedBlog.slug}`);
                        }}>
                        <img
                          src={getImageUrl(
                            relatedBlog.featuredImage.url,
                            'small'
                          )}
                          alt={
                            relatedBlog.featuredImage.alt || relatedBlog.title
                          }
                          className="object-cover w-full h-48 transition-all duration-200"
                        />
                        <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-10">
                          <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getJobTypeColor(
                          relatedBlog.jobType
                        )} mb-3`}>
                        {relatedBlog.jobType}
                      </span>
                      <h3 className="mb-3 text-xl font-semibold leading-tight text-gray-900 line-clamp-2">
                        {relatedBlog.title}
                      </h3>
                      <p className="mb-4 leading-relaxed text-gray-600 line-clamp-3">
                        {relatedBlog.excerpt || relatedBlog.introParagraph}
                      </p>
                      <Link
                        to={`/blog/${relatedBlog.slug}`}
                        className="inline-flex items-center text-base font-medium text-primary hover:text-primary-hover">
                        Read More
                        <svg
                          className="ml-1 w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Back to Blog */}
        <div className="bg-white border-t border-gray-200">
          <div className="px-4 py-12 mx-auto max-w-6xl sm:px-6 lg:px-8">
            <div className="text-center">
              <Link
                to="/blog"
                className="inline-flex items-center px-8 py-4 text-lg font-medium bg-white rounded-lg border-2 transition-colors duration-200 border-primary text-primary hover:bg-primary hover:text-white">
                <svg
                  className="mr-3 w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Blog
              </Link>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {modalOpen && allImages.length > 0 && (
          <ImageModal
            images={allImages}
            currentIndex={selectedImageIndex}
            onClose={() => setModalOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default BlogArticle;
